import type {
  BreakpointBasedText,
  CashbackCampaign,
  CashbackContents,
  CombinedCampaign,
  CombinedContents,
  DiscountContents,
  GraphQLResponse,
  TourOperatorDiscountCampaign,
} from '@/modules/common/hooks/useCampaigns/types';
import type {Meta} from '@/redux/v1/models';
import type {Breakpoint} from '@travel-frontend/themes';

import {INVIA_TYPE_DISCOUNT} from '@/consts';
import {createDirectusAssetUrl} from '@/utils/createDirectusAssetUrl';

export const contentMapper = <
  Element extends Record<string, BreakpointBasedText>,
>(
  listOfContent: Array<Element>,
  key: keyof Element
) =>
  listOfContent.reduce<Record<Breakpoint, string>>(
    (titles, current) => {
      for (const bp of current[key].breakpoints) {
        titles[bp] = current[key].value;
      }
      return titles;
    },
    {xs: '', sm: '', md: '', lg: '', xl: ''}
  );

export const graphQLResponseToTourOperatorDiscountCampaign = (
  responseData: GraphQLResponse['data']['tour_operator_discount_campaign']
): Array<TourOperatorDiscountCampaign> => {
  return responseData.map((campaign) => {
    const text = campaign.interceptorContent.split(' ');
    const highlight = text.pop() ?? '';

    let interceptorContentOffers: TourOperatorDiscountCampaign['interceptorContentOffers'] =
      undefined;

    if (campaign.interceptorContentOffers) {
      const interceptorContentOffersText =
        campaign.interceptorContentOffers.split(' ');
      const interceptorContentOffersHighlight =
        interceptorContentOffersText.pop() ?? '';

      interceptorContentOffers = {
        text: interceptorContentOffersText.join(' '),
        highlight: interceptorContentOffersHighlight,
      };
    }

    let interceptorContentBestOffers: TourOperatorDiscountCampaign['interceptorContentBestOffers'] =
      undefined;

    if (campaign.interceptorContentBestOffers) {
      const interceptorContentBestOffersText =
        campaign.interceptorContentBestOffers.split(' ');
      const interceptorContentBestOffersHighlight =
        interceptorContentBestOffersText.pop() ?? '';

      interceptorContentBestOffers = {
        text: interceptorContentBestOffersText.join(' '),
        highlight: interceptorContentBestOffersHighlight,
      };
    }

    return {
      ...campaign,
      interceptorContentOffers,
      interceptorContentBestOffers,
      interceptorContent: {
        text: text.join(' '),
        highlight,
      },
      tourOperators: campaign.tourOperators.map(
        (o) => o.tour_operator_id.value
      ),
      titles: contentMapper<DiscountContents[number]>(campaign.titles, 'item'),
      subtitles: contentMapper<DiscountContents[number]>(
        campaign.subtitles,
        'item'
      ),
      hint: campaign.hint,
      hintTooltip: campaign.hint_tooltip,
    };
  });
};

export const getTourOperatorsBySpecial = (
  meta?: null | Meta
): Array<string> => {
  return (
    meta?.tourOperatorSpecials?.specials?.variations?.find(
      (o) => o.type === INVIA_TYPE_DISCOUNT
    )?.tourOperators ?? []
  );
};

export const getFilteredCampaigns = (
  campaign: TourOperatorDiscountCampaign,
  tourOperators: Array<string>
) => {
  if (campaign.tourOperators.length === 0) {
    return campaign;
  }
  const matchingTourOperators = campaign.tourOperators.filter((operator) => {
    return tourOperators.includes(operator);
  });
  return matchingTourOperators.length > 0 ? campaign : null;
};

const getTextColor = (color: string) => {
  if (color === 'contentInversePrimary') return 'contentInversePrimary';
  if (color === 'contentBrand') return 'contentBrand';
  return 'contentPrimary';
};

export const graphQLResponseToCashbackCampaign = (
  responseData: GraphQLResponse['data']['cashback_campaign']
): Array<CashbackCampaign> => {
  return responseData.map((campaign) => {
    return {
      gradient: campaign.gradient,
      conditionsId: campaign.conditions?.identifier,
      interceptorText: campaign.interceptor_text,
      interceptorHighlight: campaign.interceptor_highlight,
      title: contentMapper<CashbackContents[number]>(
        campaign.title,
        'cashback_campaign_texts_id'
      ),
      subtitle: contentMapper<CashbackContents[number]>(
        campaign.subtitle,
        'cashback_campaign_texts_id'
      ),
      link: contentMapper<CashbackContents[number]>(
        campaign.link,
        'cashback_campaign_texts_id'
      ),
      hint: campaign.hint,
      hintTooltip: campaign.hint_tooltip,
      seasonal: campaign.seasonal,
      interceptorImageSmall: createDirectusAssetUrl(
        campaign.interceptor_image_small?.filename_disk
      ),
      bannerBackgroundSmall: createDirectusAssetUrl(
        campaign.banner_background_small?.filename_disk
      ),
      bannerBackgroundSmallSize: campaign.banner_background_small_size,
      bannerBackgroundSmallPosition: campaign.banner_background_small_position,
      interceptorImageLarge: createDirectusAssetUrl(
        campaign.interceptor_image_large?.filename_disk
      ),
      bannerBackgroundLarge: createDirectusAssetUrl(
        campaign.banner_background_large?.filename_disk
      ),
      bannerBackgroundLargeSize: campaign.banner_background_large_size,
      bannerBackgroundLargePosition: campaign.banner_background_large_position,
      textColor: getTextColor(campaign.text_color),
    };
  });
};

export const graphQLResponseToCombinedCampaign = (
  responseData: GraphQLResponse['data']['cashback_discount_campaign'],
  gradient: string
): Array<CombinedCampaign> => {
  return responseData.map((combined) => {
    return {
      interceptorText: combined.interceptor_text,
      interceptorHighlight: combined.interceptor_highlight,
      cashbackTitle: contentMapper<CombinedContents[number]>(
        combined.cashback_title,
        'cashback_discount_campaign_texts_id'
      ),
      cashbackLink: contentMapper<CombinedContents[number]>(
        combined.cashback_link,
        'cashback_discount_campaign_texts_id'
      ),
      discountTitle: contentMapper<CombinedContents[number]>(
        combined.discount_title,
        'cashback_discount_campaign_texts_id'
      ),
      discountButtonText: combined.discount_button_text,
      conditionsId: combined.conditions.identifier,
      hint: combined.hint,
      hintTooltip: combined.hint_tooltip,
      gradient,
    };
  });
};
